import { useState } from 'react';

/**
 * useGraphEditing Hook - JavaScript Version
 * 
 * Manages graph editing operations including adding/removing nodes and edges
 */
export const useGraphEditing = ({
  graphData,
  setGraphData,
  setGraphVersion
}) => {
  const [nextNodeId, setNextNodeId] = useState(1);
  const [edgeForm, setEdgeForm] = useState({
    source: '',
    target: '',
    weight: 1
  });

  // Add a new node to the graph
  const addNode = () => {
    try {
      // Calculate next available ID
      const maxId = Math.max(...graphData.nodes.map(n => n.id), 0);
      const newNodeId = maxId + 1;
      
      // Generate node label (N1, N2, N3, etc.)
      const nodeLabel = `N${newNodeId}`;
      
      // Calculate default position (center of canvas with some offset)
      const canvasWidth = 600;
      const canvasHeight = 500;
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;
      
      // Add some randomness to avoid overlapping
      const offsetX = (Math.random() - 0.5) * 200;
      const offsetY = (Math.random() - 0.5) * 200;
      
      const newNode = {
        id: newNodeId,
        label: nodeLabel,
        x: centerX + offsetX,
        y: centerY + offsetY
      };
      
      // Update graph data
      setGraphData({
        ...graphData,
        nodes: [...graphData.nodes, newNode]
      });
      
      // Update next node ID
      setNextNodeId(newNodeId + 1);
      
      console.log('Node added successfully:', newNode);
      
    } catch (error) {
      console.error('Failed to add node:', error);
      alert('Failed to add node. Please try again.');
    }
  };

  // Add an edge between two nodes
  const addEdge = () => {
    try {
      // Validate form data
      if (!edgeForm.source || !edgeForm.target) {
        alert('Please select both source and target nodes.');
        return;
      }
      
      if (edgeForm.source === edgeForm.target) {
        alert('Source and target nodes must be different.');
        return;
      }
      
      if (!edgeForm.weight || edgeForm.weight < 1) {
        alert('Please enter a valid weight (minimum 1).');
        return;
      }
      
      // Check if edge already exists
      const edgeExists = graphData.edges.some(edge => 
        (edge.from_ === parseInt(edgeForm.source) && edge.to === parseInt(edgeForm.target)) ||
        (edge.from_ === parseInt(edgeForm.target) && edge.to === parseInt(edgeForm.source))
      );
      
      if (edgeExists) {
        alert('An edge already exists between these nodes.');
        return;
      }
      
      // Create new edge
      const newEdge = {
        from_: parseInt(edgeForm.source),
        to: parseInt(edgeForm.target),
        length: parseInt(edgeForm.weight.toString()),
        label: `${graphData.nodes.find(n => n.id === parseInt(edgeForm.source))?.label}-${graphData.nodes.find(n => n.id === parseInt(edgeForm.target))?.label}`
      };
      
      // Update graph data
      setGraphData({
        ...graphData,
        edges: [...graphData.edges, newEdge]
      });
      
      // Reset form
      setEdgeForm({
        source: '',
        target: '',
        weight: 1
      });
      
      console.log('Edge added successfully:', newEdge);
      
    } catch (error) {
      console.error('Failed to add edge:', error);
      alert('Failed to add edge. Please try again.');
    }
  };

  // Delete a node and all its connected edges
  const deleteNode = (nodeId) => {
    try {
      // Find the node to get its label for logging
      const nodeToDelete = graphData.nodes.find(n => n.id === nodeId);
      if (!nodeToDelete) {
        console.warn(`Node with ID ${nodeId} not found`);
        return;
      }

      // Count connected edges for logging
      const connectedEdges = graphData.edges.filter(edge => 
        edge.from_ === nodeId || edge.to === nodeId
      );

      // Remove the node
      const updatedNodes = graphData.nodes.filter(node => node.id !== nodeId);
      
      // Remove all edges connected to this node
      const updatedEdges = graphData.edges.filter(edge => 
        edge.from_ !== nodeId && edge.to !== nodeId
      );

      // Update graph data
      setGraphData({
        ...graphData,
        nodes: updatedNodes,
        edges: updatedEdges
      });

      // Force re-render
      setGraphVersion(Date.now());

      console.log(`Node ${nodeToDelete.label} deleted successfully. Removed ${connectedEdges.length} connected edges.`);
      
    } catch (error) {
      console.error('Failed to delete node:', error);
      alert('Failed to delete node. Please try again.');
    }
  };

  // Delete node with confirmation dialog
  const deleteNodeWithConfirmation = (nodeId) => {
    const nodeToDelete = graphData.nodes.find(n => n.id === nodeId);
    if (!nodeToDelete) {
      console.warn(`Node with ID ${nodeId} not found`);
      return;
    }

    // Count connected edges
    const connectedEdges = graphData.edges.filter(edge => 
      edge.from_ === nodeId || edge.to === nodeId
    );

    // Create confirmation message
    const edgeCount = connectedEdges.length;
    const edgeText = edgeCount === 1 ? 'edge' : 'edges';
    const message = `Are you sure you want to delete node "${nodeToDelete.label}"?\n\nThis will also remove ${edgeCount} connected ${edgeText}.\n\nThis action cannot be undone.`;

    if (window.confirm(message)) {
      deleteNode(nodeId);
    }
  };

  // Export graph as JSON file
  const exportGraph = () => {
    try {
      // Create export data with metadata
      const exportData = {
        name: "Custom Graph",
        description: "Exported from LogiSim Shortest Path Module",
        timestamp: new Date().toISOString(),
        version: "1.0",
        data: {
          nodes: graphData.nodes,
          edges: graphData.edges
        }
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `graph-${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log('Graph exported successfully');
    } catch (error) {
      console.error('Failed to export graph:', error);
      alert('Failed to export graph. Please try again.');
    }
  };

  // Import graph from JSON file
  const importGraph = () => {
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.style.display = 'none';
    
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target?.result);
          
          // Validate the imported data structure
          if (!jsonData.data || !jsonData.data.nodes || !jsonData.data.edges) {
            throw new Error('Invalid graph file format. Expected nodes and edges in data property.');
          }
          
          // Validate nodes structure
          if (!Array.isArray(jsonData.data.nodes)) {
            throw new Error('Nodes must be an array.');
          }
          
          // Validate edges structure
          if (!Array.isArray(jsonData.data.edges)) {
            throw new Error('Edges must be an array.');
          }
          
          // Basic validation of node structure
          jsonData.data.nodes.forEach((node, index) => {
            if (!node.id || !node.label || node.x === undefined || node.y === undefined) {
              throw new Error(`Invalid node at index ${index}. Required: id, label, x, y`);
            }
          });
          
          // Basic validation of edge structure
          jsonData.data.edges.forEach((edge, index) => {
            if (!edge.from_ || !edge.to || edge.length === undefined) {
              throw new Error(`Invalid edge at index ${index}. Required: from_, to, length`);
            }
          });
          
          // Update graph data
          setGraphData({
            nodes: jsonData.data.nodes,
            edges: jsonData.data.edges
          });
          
          // Force re-render by incrementing version
          setGraphVersion(Date.now());
          
          console.log('Graph imported successfully:', jsonData.name || 'Unknown');
          alert(`Graph imported successfully! Loaded ${jsonData.data.nodes.length} nodes and ${jsonData.data.edges.length} edges.`);
          
        } catch (error) {
          console.error('Failed to import graph:', error);
          alert(`Failed to import graph: ${error.message}`);
        }
      };
      
      reader.onerror = () => {
        alert('Failed to read the file. Please try again.');
      };
      
      reader.readAsText(file);
    };
    
    // Trigger file selection
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  return {
    nextNodeId,
    setNextNodeId,
    edgeForm,
    setEdgeForm,
    addNode,
    addEdge,
    deleteNode,
    deleteNodeWithConfirmation,
    exportGraph,
    importGraph
  };
};
